import {
  Combo,
  VehicleCategoriesEnum,
  SourcePartnerEnum,
  VehicleSearchOptions,
  VehicleSearchOptionsProps,
} from "c4u-web-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useKbb, useRenaveContext } from "../../../hooks";

interface IProps {
  vehicleKbbId?: string;
  vehicleMolicarId?: string;
  molicarID?: string;
  className?: string;
  isZeroKm?: boolean;
  sourcePartner: SourcePartnerEnum;
  setSourcePartner: (sourcePartner: SourcePartnerEnum) => void;
  setHasSelectedCategory: (value: boolean) => void;
}

export const StaticVehicleOptions: React.FC<IProps> = (parameters) => {
  const { t } = useTranslation();

  const [props, setProps] = useState(parameters);
  useEffect(() => {
    setProps(parameters);
  }, [parameters]);

  const {
    kbbVehicleContext,
    setKbbVehicleContext,
    vehicleCategoryContext,
    sourcePartnerContext,
    setSourcePartnerContext,
  } = useRenaveContext();

  const { GetVehicle } = useKbb();

  const [vehicleBrandCombo, setVehicleBrandCombo] = useState<Combo[]>([]);
  const [vehicleModelCombo, setVehicleModelCombo] = useState<Combo[]>([]);
  const [vehicleVersionCombo, setVehicleVersionCombo] = useState<Combo[]>([]);
  const [yearsCombo, setYearsCombo] = useState<Combo[]>([]);

  const [haveYearsSelected, setHaveYearsSelected] = useState(false);

  const isSourceKbb = useMemo(
    () => sourcePartnerContext === SourcePartnerEnum.Kbb,
    [sourcePartnerContext]
  );
  const isCarCategory = useMemo(
    () => vehicleCategoryContext === VehicleCategoriesEnum.Car,
    [vehicleCategoryContext]
  );
  const hasVersionIdOrName = !!(kbbVehicleContext?.versionID || kbbVehicleContext?.versionName)

  const getVehicleAsync = useCallback(
    async (
      id: number,
      molicarID: string | null,
      year: number
    ): Promise<void> => {
      if (!hasVersionIdOrName) {
        const vehicle = await GetVehicle(id, molicarID, year);
        setKbbVehicleContext(vehicle);
      }
    },
    [GetVehicle, hasVersionIdOrName, setKbbVehicleContext]
  );

  useEffect(() => {
    if (isCarCategory) {
      setSourcePartnerContext(SourcePartnerEnum.Kbb);
    } else {
      setSourcePartnerContext(SourcePartnerEnum.Molicar);
    }
  }, [isCarCategory, setSourcePartnerContext, sourcePartnerContext]);

  useEffect(() => {
    if (kbbVehicleContext !== undefined && !vehicleVersionCombo) {
      if (isSourceKbb) {
        getVehicleAsync(
          Number(kbbVehicleContext.id),
          null,
          kbbVehicleContext.year
        );
      } else {
        getVehicleAsync(0, kbbVehicleContext.molicarID, kbbVehicleContext.year);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (kbbVehicleContext?.year === 0) {
      setHaveYearsSelected(false);
    } else {
      setHaveYearsSelected(true);
    }
  }, [kbbVehicleContext?.year]);

  useEffect(() => {
    vehicleBrandCombo.length > 0
      ? props.setHasSelectedCategory(true)
      : props.setHasSelectedCategory(false);
  }, [props, vehicleBrandCombo]);

  useEffect(() => {
    if (hasVersionIdOrName) {
      setVehicleBrandCombo([
        new Combo({
          title: kbbVehicleContext.brandName,
          value: kbbVehicleContext.brandID,
        }),
      ]);

      setVehicleModelCombo([
        new Combo({
          title: kbbVehicleContext.modelName,
          value: kbbVehicleContext.modelID,
        }),
      ]);

      setVehicleVersionCombo([
        new Combo({
          title: kbbVehicleContext.versionName,
          value: isSourceKbb
            ? kbbVehicleContext.id
            : kbbVehicleContext.molicarID,
        }),
      ]);

      setYearsCombo([
        new Combo({
          title:
            kbbVehicleContext.year === 2023
              ? `${kbbVehicleContext.year}*`
              : kbbVehicleContext.year,
          value: kbbVehicleContext.year,
        }),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kbbVehicleContext]);

  const propsVehicleSearchOptions = useMemo((): VehicleSearchOptionsProps => {
    return {
      Dropdowns: {
        showAll: true,
        haveYearsSelected: haveYearsSelected,
        Brand: {
          placeholder: t("Select") + "...",
          Options: vehicleBrandCombo,
          Value: vehicleBrandCombo[0],
          disabled: true,
          dataCyid: "brand",
        },
        Model: {
          placeholder: t("Select") + "...",
          Options: vehicleModelCombo,
          Value: vehicleModelCombo[0],
          disabled: true,
        },
        Version: {
          placeholder: t("Select") + "...",
          Options: vehicleVersionCombo,
          Value: vehicleVersionCombo[0],
          disabled: true,
        },
        Year: {
          placeholder: t("Select") + "...",
          Options: yearsCombo,
          Value: yearsCombo[0],
          disabled: true,
        },
      },
      Texts: {
        Brand: t("Brand"),
        ClearFilter: t("Clean Filters"),
        Loading: t("Loading"),
        Model: t("Model"),
        NoMatchesFound: t("NoMatchesFound"),
        Others: t("Others"),
        TitleYears: t("ModelYear"),
        Version: t("Version"),
      },
    };
    // eslint-disable-next-line
  }, [
    vehicleBrandCombo,
    yearsCombo,
    vehicleModelCombo,
    vehicleVersionCombo,
    t,
    haveYearsSelected,
  ]);

  return (
    <Row>
      <Col>
        <VehicleSearchOptions {...propsVehicleSearchOptions} />
      </Col>
    </Row>
  );
};
